import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { FileStack, ArrowRight, Globe, BarChart2, RefreshCw, CheckCircle2 } from 'lucide-react';
interface SlideWhereThingsBreakProps {
  onContinue: () => void;
}
interface BreakCard {
  icon: React.ReactNode;
  label: string;
  items: string[];
  micro: string;
}
export function SlideWhereThingsBreak({
  onContinue
}: SlideWhereThingsBreakProps) {
  const [activeCard, setActiveCard] = useState(-1);
  const [showBottom, setShowBottom] = useState(false);
  const [showInsight, setShowInsight] = useState(false);
  const cards: BreakCard[] = [{
    icon: <FileStack className="w-5 h-5" data-id="element-459" />,
    label: 'Content',
    items: ['Slides', 'PDFs', 'Videos'],
    micro: 'Built to show — not engage'
  }, {
    icon: <ArrowRight className="w-5 h-5" data-id="element-460" />,
    label: 'Campaign',
    items: ['One message', 'One push'],
    micro: 'No progression'
  }, {
    icon: <Globe className="w-5 h-5" data-id="element-461" />,
    label: 'Local',
    items: ['Rework', 'Rebuild'],
    micro: 'Slow / expensive'
  }, {
    icon: <BarChart2 className="w-5 h-5" data-id="element-462" />,
    label: 'Data',
    items: ['Opens', 'Clicks'],
    micro: 'No depth'
  }, {
    icon: <RefreshCw className="w-5 h-5" data-id="element-463" />,
    label: 'Improve',
    items: ['Late', 'Reports'],
    micro: 'Not applied'
  }];
  useEffect(() => {
    const timers = cards.map((_, idx) => setTimeout(() => setActiveCard(idx), 500 + idx * 600));
    const bottomTimer = setTimeout(() => setShowBottom(true), 500 + cards.length * 600 + 300);
    const insightTimer = setTimeout(() => setShowInsight(true), 500 + cards.length * 600 + 600);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(bottomTimer);
      clearTimeout(insightTimer);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-14 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-464">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-10 text-center" data-id="element-465">
        Where things <span className="text-white/50" data-id="element-466">break</span> today
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-id="element-467">
        {/* 5 Cards */}
        <div className="lg:col-span-9 relative" data-id="element-468">
          {/* Fragile connecting line */}
          <div className="absolute top-12 left-[10%] right-[10%] h-[2px] z-0 flex items-center" data-id="element-469">
            {[0, 1, 2, 3].map(i => <motion.div key={i} className="flex-1 flex items-center" initial={{
            opacity: 0
          }} animate={{
            opacity: i <= activeCard ? 0.4 : 0
          }} data-id="element-470">
                <div className="flex-1 h-[1px] bg-white/15 mx-1" style={{
              backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 6px, transparent 6px, transparent 12px)'
            }} data-id="element-471" />
              </motion.div>)}
          </div>

          <div className="grid grid-cols-5 gap-3 relative z-10" data-id="element-472">
            {cards.map((card, idx) => {
            const isActive = idx <= activeCard;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: isActive ? 1 : 0.15,
              y: isActive ? 0 : 15
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center text-center" data-id="element-473">
                  {/* Icon */}
                  <motion.div animate={{
                backgroundColor: isActive ? '#1E2D4A' : '#151E32',
                borderColor: isActive ? '#3A5278' : '#1E2D4A',
                scale: isActive ? 1 : 0.8
              }} className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-white/40 mb-3" data-id="element-474">
                    {card.icon}
                  </motion.div>

                  {/* Card */}
                  <div className={`rounded-xl p-3 w-full transition-all ${isActive ? 'bg-navy-700/40 border border-navy-600' : 'bg-navy-800/20 border border-navy-700/20'}`} data-id="element-475">
                    <h4 className={`font-semibold text-xs mb-2 ${isActive ? 'text-white/70' : 'text-white/20'}`} data-id="element-476">
                      {card.label}
                    </h4>

                    <div className="space-y-1 mb-2" data-id="element-477">
                      {card.items.map((item, i) => <div key={i} className={`text-xs ${isActive ? 'text-white/50' : 'text-white/15'}`} data-id="element-478">
                          {item}
                        </div>)}
                    </div>

                    <p className={`text-xs italic border-t pt-1.5 ${isActive ? 'text-red-400/50 border-navy-600' : 'text-white/10 border-navy-700/30'}`} data-id="element-479">
                      {card.micro}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Bottom hero statement */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: showBottom ? 1 : 0,
          y: showBottom ? 0 : 10
        }} className="mt-8 text-center" data-id="element-480">
            <p className="text-lg text-white/60 font-medium" data-id="element-481">
              None of this is a capability problem.
            </p>
            <p className="text-xl text-teal-400 font-serif mt-1" data-id="element-482">
              It's structural friction.
            </p>
          </motion.div>
        </div>

        {/* Insight Box */}
        <div className="lg:col-span-3" data-id="element-483">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: showInsight ? 1 : 0,
          x: showInsight ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/50 border border-navy-600 p-6 rounded-2xl" data-id="element-484">
            <div className="text-teal-400 font-medium mb-4 flex items-center gap-2 text-sm" data-id="element-485">
              <CheckCircle2 className="w-4 h-4" data-id="element-486" />
              Insight
            </div>
            <div className="text-lg leading-relaxed text-white/90 space-y-3" data-id="element-487">
              <p className="font-medium" data-id="element-488">You're doing the right things.</p>
              <p className="text-white/70" data-id="element-489">
                <span className="text-teal-400 mr-1" data-id="element-490">→</span>
                They just don't connect
              </p>
            </div>

            <div className="mt-6" data-id="element-491">
              <Button onClick={onContinue} data-id="element-492">What connects them</Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}